/**
 * Iter-52.54 — Exercises panel v4.
 *
 * Changes on top of Iter-52.53:
 *   • Grouping is now by the TEXTBOOK'S OWN section (backend emits
 *     section_key + section_heading + section_order per question).
 *     No more generic "Answer the following / Fill in the blanks"
 *     hard-coded labels — we render whatever the textbook printed.
 *   • Each question uses its printed item label ("2. a)", "5. c) (ii)")
 *     — no more Q1/Q2/… numbering imposed by the UI.
 *   • Answer Key is REMOVED from this screen. A "🔑 See Answer Key /
 *     उत्तर कुंजी देखें" button at the bottom navigates to the
 *     dedicated AnswerKeyScreen so children can't shortcut past
 *     attempting a question.
 *   • Karaoke highlight upgraded to match Read-to-Me visuals
 *     (soft amber bg + orange ring + shadow) — the subtle pill was
 *     hard to spot on production.
 *   • Fetch: 20s timeout via AbortController + retry once. If the
 *     retry also fails, we surface a "Couldn't load exercises — tap
 *     to try again" state instead of a permanent spinner.
 *   • Ghost questions (Q# with no text) are already dropped server-
 *     side; a frontend safety net keeps them out even from stale
 *     caches. Any question whose answer failed to generate gets a
 *     compact "Answer not available — tap to retry" affordance on
 *     the Answer Key screen (not here).
 */
import { useEffect, useMemo, useRef, useState, useCallback, Fragment } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { api, API } from '../lib/api';
import { isPictureItem } from '../lib/textMarkers';
import useTts from '../lib/useTts';
import SyllablePopover from './SyllablePopover';
import TappableText from './TappableText';
import {
  Loader2, Volume2, VolumeX, Pencil, Trash2, BookOpen, KeyRound,
} from 'lucide-react';

const HAS_HIDDEN_WRITE = new Set(['long_answer', 'critical_thinking']);
const isDevanagari = (s) => /[\u0900-\u097F]/.test(s || '');
// Iter-52.55 — backend crop URLs come back as `/api/uploads/match/<id>.png`
// so we always prefix with the runtime backend base URL. Anything that
// is already an absolute URL (starts with http/https/data:) is passed
// through unchanged so match-block sentinel URLs keep working.
const backendUrl = (p) => {
  if (!p) return '';
  const s = String(p);
  if (/^(https?:|data:)/i.test(s)) return s;
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  return `${base}${s.startsWith('/') ? '' : '/'}${s}`;
};

/** 🔊 speaker button with karaoke playback + auto-stop-others. */
function SpeakerBtn({ text, voiceGender, tts, size = 14, testId, id, onPlayStart, onWordChange, activePlaybackId }) {
  if (!text || !text.trim()) return null;
  const isThisPlaying = activePlaybackId === id;
  const busy = tts.loading && isThisPlaying;
  const playing = tts.playing && isThisPlaying;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={(e) => {
        e.stopPropagation();
        if (isThisPlaying) { tts.stop(); onWordChange && onWordChange(-1); return; }
        tts.stop();
        onPlayStart && onPlayStart(id);
        const lang = isDevanagari(text) ? 'hi' : 'en';
        tts.play(text, {
          language: lang, speed: 0.9, voice_gender: voiceGender || 'female',
          withTimestamps: true,
          onWordChange: (idx) => { onWordChange && onWordChange(idx, id); },
        });
      }}
      className={`inline-flex items-center justify-center rounded-full w-6 h-6 border shrink-0 ${busy || playing ? 'bg-[#FF6B1A] text-white border-[#FF6B1A]' : 'bg-white text-[#5B8DEF] border-[#5B8DEF]/40 hover:border-[#5B8DEF]'}`}
      aria-label="Read aloud"
    >
      {busy ? <Loader2 size={size} className="animate-spin"/>
        : playing ? <VolumeX size={size} strokeWidth={2.5}/>
        : <Volume2 size={size} strokeWidth={2.5}/>}
    </button>
  );
}

/** Text row with speaker + karaoke + tap-the-word. */
function SpokenText({
  text, id, activePlaybackId, activeWordIdx, onWordTap, onPlayStart, onWordChange,
  voiceGender, tts, className = '', testId, style,
}) {
  if (!text || !text.trim()) return null;
  const isThisActive = activePlaybackId === id;
  return (
    <div className="flex items-start gap-2">
      <SpeakerBtn id={id} text={text} voiceGender={voiceGender} tts={tts}
        activePlaybackId={activePlaybackId}
        onPlayStart={onPlayStart} onWordChange={onWordChange}/>
      <TappableText
        text={text}
        testId={testId}
        className={`flex-1 min-w-0 break-words leading-snug ${className}`}
        style={{ textAlign: 'left', hyphens: 'auto', ...(style || {}) }}
        activeWordIdx={isThisActive ? activeWordIdx : -1}
        onWordTap={(w) => onWordTap && onWordTap(w, text)}
      />
    </div>
  );
}

/** 🗑️ Clear button with native confirm(). */
function ClearBtn({ onClear, testId, disabled }) {
  return (
    <button type="button" data-testid={testId} disabled={disabled}
      onClick={() => {
        if (window.confirm('Are you sure you want to clear this answer? / क्या आप इसे मिटाना चाहते हैं?')) onClear();
      }}
      className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-[#B85582] disabled:opacity-40 px-2 py-1 rounded-full border border-slate-200 hover:border-[#B85582]/40">
      <Trash2 size={11} strokeWidth={2.5}/> Clear / मिटाएँ
    </button>
  );
}

// Debounce helper — one save per (child, page, question) at most every 800ms.
function useDebouncedCallback(fn, delay) {
  const t = useRef();
  return useCallback((...args) => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => fn(...args), delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delay]);
}

// ─── Iter-52.55.5 — Match + Rewrite worksheets ───────────────────────
//
// Some Match-the-Following worksheets carry a second printed
// instruction: "Rewrite the correct sentence" (or Hindi "फिर सही
// वाक्य लिखिए"). The detect prompt sets `q.has_rewrite = true` and
// `q.followup_instruction = "..."` on the match_the_following entry;
// this component renders one collapsible ✍️ writing box per Column A
// item beneath the match grid. Each box is persisted per-child /
// per-page under `rewrite_texts[qid][row_idx]` via /exercises/scratch.
function RewriteSection({
  q, childId, bookId, pageId, initialRewriteTexts,
  pageImage, onOpenImage, onScratchChange,
  tts, voiceGender, activePlaybackId, onPlayStart, onWordChange, onWordTap,
}) {
  const [texts, setTexts] = useState(initialRewriteTexts || {});
  const canPersist = !!childId && !!bookId && !!pageId;
  const persist = useRef();
  const persistRow = (rowIdx, value) => {
    // Piggy-back on the shared 400 ms debounce from useDebouncedCallback.
    if (!persist.current) {
      persist.current = {};
    }
    if (persist.current[rowIdx]) clearTimeout(persist.current[rowIdx]);
    persist.current[rowIdx] = setTimeout(async () => {
      onScratchChange && onScratchChange(JSON.stringify({ ...(texts || {}), [rowIdx]: value }));
      if (!canPersist) return;
      try {
        await api.post('/exercises/scratch', {
          child_id: childId, book_id: bookId, page_id: pageId,
          question_id: q.id, rewrite_texts: { [String(rowIdx)]: value },
        });
      } catch {}
    }, 600);
  };
  const clearRow = async (rowIdx) => {
    if (!window.confirm('Are you sure you want to clear this answer? / क्या आप इसे मिटाना चाहते हैं?')) return;
    setTexts(prev => { const nxt = { ...prev }; delete nxt[String(rowIdx)]; return nxt; });
    if (!canPersist) return;
    try {
      await api.post('/exercises/scratch', {
        child_id: childId, book_id: bookId, page_id: pageId,
        question_id: q.id, rewrite_texts: { [String(rowIdx)]: '' },
      });
    } catch {}
  };
  const followup = (q.followup_instruction || '').trim();
  const followupMeaning = (q.followup_instruction_meaning || '').trim();
  return (
    <div className="mt-3 rounded-xl border-2 border-[#B85582]/30 bg-[#FFF7EA]/60 p-2 md:p-3"
      data-testid={`exercise-rewrite-section-${q.id}`}>
      {followup ? (
        <SpokenText
          text={followup}
          id={`rw-instr-${q.id}`}
          activePlaybackId={activePlaybackId}
          onPlayStart={onPlayStart} onWordChange={onWordChange} onWordTap={onWordTap}
          voiceGender={voiceGender} tts={tts}
          testId={`exercise-rewrite-instruction-${q.id}`}
          className="text-sm md:text-base font-black text-[#B85582]"
        />
      ) : (
        <p className="text-sm md:text-base font-black text-[#B85582]">
          ✍️ Rewrite the correct sentence / सही वाक्य लिखिए
        </p>
      )}
      {followupMeaning && (
        <div className="ml-8 mt-0.5">
          <SpokenText
            text={followupMeaning}
            id={`rw-instrm-${q.id}`}
            activePlaybackId={activePlaybackId}
            onPlayStart={onPlayStart} onWordChange={onWordChange} onWordTap={onWordTap}
            voiceGender={voiceGender} tts={tts}
            className="text-xs md:text-sm italic text-slate-500"
          />
        </div>
      )}
      <div className="mt-2 space-y-2">
        {q.left.map((leftItem, i) => {
          const leftImg = q.left_images?.[i] || '';
          const isPic = isPictureItem(leftItem, leftImg);
          const val = texts[String(i)] || '';
          return (
            <div key={i} data-testid={`exercise-rewrite-row-${q.id}-${i}`}
              className="rounded-lg bg-white border border-slate-200 p-2 flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-black text-[#FF6B1A] shrink-0">{i + 1}.</span>
                {isPic ? (
                  leftImg ? (
                    <img src={leftImg} alt=""
                      onClick={() => onOpenImage && onOpenImage(leftImg)}
                      className="h-12 w-16 object-contain bg-white rounded border cursor-zoom-in"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
                  ) : (
                    <span className="text-[10px] italic text-slate-500">Picture load ho rahi hai…</span>
                  )
                ) : (
                  <span className="text-xs md:text-sm font-bold text-[#2B2140] break-words">{leftItem}</span>
                )}
                {val && (
                  <button type="button" onClick={() => clearRow(i)}
                    data-testid={`exercise-rewrite-clear-${q.id}-${i}`}
                    className="ml-auto shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 hover:text-[#B85582] px-1.5 py-0.5 rounded-full border border-slate-200 hover:border-[#B85582]/40">
                    <Trash2 size={10} strokeWidth={2.5}/> Clear
                  </button>
                )}
              </div>
              <textarea
                data-testid={`exercise-rewrite-input-${q.id}-${i}`}
                value={val}
                onChange={e => {
                  const nv = e.target.value;
                  setTexts(prev => ({ ...prev, [String(i)]: nv }));
                  persistRow(i, nv);
                }}
                placeholder="✍️ Write the sentence / वाक्य लिखें"
                rows={2}
                className="w-full px-2 py-1.5 rounded-lg border border-slate-300 text-sm md:text-base text-[#2B2140] focus:outline-none focus:border-[#FF6B1A] resize-y"
                style={{ overflowWrap: 'anywhere', wordBreak: 'normal', hyphens: 'auto' }}/>
            </div>
          );
        })}
      </div>
    </div>
  );
}



function ExerciseCard({ q, tts, childId, bookId, pageId, voiceGender, initialScratch, pageImage,
  activePlaybackId, activeWordIdx, onPlayStart, onWordChange, onWordTap, onOpenImage,
  verdictInfo, onScratchChange }) {
  const isFill = q.type === 'fill_blank';
  const isMcq = q.type === 'mcq';
  const isMatch = q.type === 'match_the_following';
  const isUnscramble = q.type === 'unscramble';
  const hiddenWrite = HAS_HIDDEN_WRITE.has(q.type);

  const [attempt, setAttempt] = useState(initialScratch?.text || '');
  const [selectedIndex, setSelectedIndex] = useState(initialScratch?.selected ?? -1);
  const [pairs, setPairs] = useState(initialScratch?.pairs || {});
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [expandedWrite, setExpandedWrite] = useState(!hiddenWrite || !!initialScratch?.text);

  const canPersist = !!childId && !!bookId && !!pageId;
  // Iter-52.55.4 — every persistence call also lets the parent
  // invalidate the stale ✓/✗ badge as soon as the child touches the
  // answer. `snapshotEncoder` gives us a comparable string so the
  // panel can compare against the last-checked snapshot.
  const notifyChange = (snapshot) => { onScratchChange && onScratchChange(snapshot); };
  const persistText = useDebouncedCallback(async (text) => {
    notifyChange(text);
    if (!canPersist) return;
    try { await api.post('/exercises/scratch', { child_id: childId, book_id: bookId, page_id: pageId, question_id: q.id, text }); } catch {}
  }, 800);
  const persistSelected = async (idx) => {
    notifyChange(String(idx));
    if (!canPersist) return;
    try { await api.post('/exercises/scratch', { child_id: childId, book_id: bookId, page_id: pageId, question_id: q.id, selected_index: idx }); } catch {}
  };
  const persistPairs = useDebouncedCallback(async (p) => {
    notifyChange(JSON.stringify(p));
    if (!canPersist) return;
    try { await api.post('/exercises/scratch', { child_id: childId, book_id: bookId, page_id: pageId, question_id: q.id, pairs: p }); } catch {}
  }, 400);

  const clearAll = async () => {
    setAttempt(''); setSelectedIndex(-1); setPairs({}); setSelectedLeft(null);
    notifyChange('');
    if (!canPersist) return;
    try { await api.post('/exercises/scratch', { child_id: childId, book_id: bookId, page_id: pageId, question_id: q.id, clear: true }); } catch {}
    toast.success('Cleared.');
  };

  const handleLeftTap = (i) => setSelectedLeft(selectedLeft === i ? null : i);
  const handleRightTap = (rIdx) => {
    if (selectedLeft == null) return;
    setPairs(prev => {
      const clean = Object.fromEntries(Object.entries(prev).filter(([, v]) => v !== rIdx));
      clean[selectedLeft] = rIdx;
      persistPairs(clean);
      return clean;
    });
    setSelectedLeft(null);
  };

  const label = (q.item_label || '').trim();
  return (
    <div data-testid={`exercise-card-${q.id}`} className="bg-white rounded-2xl border border-slate-200 p-3 md:p-4">
      <div className="flex items-start gap-2">
        {label && (
          <span data-testid={`exercise-item-label-${q.id}`}
            className="mt-1 text-[11px] md:text-xs font-black text-[#B85582] shrink-0 min-w-[1.75rem]">
            {label}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <SpokenText
            text={q.question}
            id={`q-${q.id}`}
            activePlaybackId={activePlaybackId}
            activeWordIdx={activeWordIdx}
            onPlayStart={onPlayStart} onWordChange={onWordChange} onWordTap={onWordTap}
            voiceGender={voiceGender} tts={tts}
            testId={`exercise-question-${q.id}`}
            className="text-base md:text-lg font-bold text-[#2B2140]"
          />
          {q.question_meaning && (
            <div className="mt-1 ml-8">
              <SpokenText
                text={q.question_meaning}
                id={`qm-${q.id}`}
                activePlaybackId={activePlaybackId}
                activeWordIdx={activeWordIdx}
                onPlayStart={onPlayStart} onWordChange={onWordChange} onWordTap={onWordTap}
                voiceGender={voiceGender} tts={tts}
                testId={`exercise-question-meaning-${q.id}`}
                className="text-xs md:text-sm italic text-slate-500"
              />
            </div>
          )}
          {q.references_image && (q.question_image ? (
            <button type="button"
              data-testid={`exercise-question-image-${q.id}`}
              onClick={() => onOpenImage && onOpenImage(backendUrl(q.question_image))}
              className="ml-8 mt-2 block rounded-xl overflow-hidden border-2 border-[#5B8DEF]/40 hover:border-[#5B8DEF] bg-white shadow-sm">
              <img src={backendUrl(q.question_image)} alt={q.question || 'referenced'}
                className="max-h-56 w-auto max-w-full object-contain block mx-auto"
                onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}/>
            </button>
          ) : (pageImage && (
            <button type="button"
              data-testid={`exercise-image-ref-${q.id}`}
              onClick={() => onOpenImage && onOpenImage(pageImage)}
              className="ml-8 mt-2 inline-flex items-center gap-2 rounded-full bg-[#FFF3CD] text-[#A76A00] border border-[#F4B740] px-3 py-1 text-xs md:text-sm font-extrabold hover:bg-[#FFE49A]">
              🖼️ Picture is in your textbook page — tap to view
            </button>
          )))}
        </div>
      </div>

      <div className="mt-3">
        {isFill && (
          <div className="flex items-center gap-2">
            <input type="text" data-testid={`exercise-attempt-${q.id}`}
              value={attempt}
              onChange={e => { setAttempt(e.target.value); persistText(e.target.value); }}
              placeholder="Type the word or phrase…"
              className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-slate-300 text-sm md:text-base font-medium text-[#2B2140] focus:outline-none focus:border-[#FF6B1A]"/>
            <ClearBtn testId={`exercise-clear-${q.id}`} onClear={clearAll} disabled={!attempt}/>
          </div>
        )}
        {isUnscramble && (
          <div className="flex items-center gap-2">
            <input type="text" data-testid={`exercise-attempt-${q.id}`}
              value={attempt}
              onChange={e => { setAttempt(e.target.value); persistText(e.target.value); }}
              placeholder="Type the correctly spelled word…"
              className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-slate-300 text-sm md:text-base font-bold uppercase tracking-wider text-[#2B2140] focus:outline-none focus:border-[#FF6B1A]"/>
            <ClearBtn testId={`exercise-clear-${q.id}`} onClear={clearAll} disabled={!attempt}/>
          </div>
        )}
        {isMcq && (
          <div className="flex flex-col gap-2" data-testid={`exercise-mcq-options-${q.id}`}>
            {(() => {
              // Iter-52.55.2 — prefer textbook-native option labels
              // (`(i)`, `(ii)`, `a)`, etc.) over the generic A/B/C/D
              // fallback so options like "Both (i) and (ii)" line up
              // with what the child sees on the printed page.
              const useTextbookLabels = Array.isArray(q.option_labels) && q.option_labels.length === (q.options || []).length;
              const labelFor = (i) => (useTextbookLabels ? q.option_labels[i] : `${String.fromCharCode(65 + i)}.`);
              return null;
            })()}
            {/* Iter-52.55 — Picture options render as a 2xN grid of
                image tiles labelled A/B/C/D. Falls back to text
                buttons when we don't have crop URLs yet. */}
            {q.options_are_pictures && Array.isArray(q.options_images) && q.options_images.some(Boolean) ? (() => {
              const useTextbookLabels = Array.isArray(q.option_labels) && q.option_labels.length === q.options.length;
              const labelFor = (i) => (useTextbookLabels ? q.option_labels[i] : `${String.fromCharCode(65 + i)}.`);
              const parseList = () => (typeof selectedIndex === 'string'
                ? selectedIndex.split(',').filter(Boolean).map(Number)
                : (typeof selectedIndex === 'number' && selectedIndex >= 0 ? [selectedIndex] : []));
              const active = new Set(parseList());
              const toggle = (i) => {
                if (q.multi_select) {
                  const nxt = new Set(active);
                  if (nxt.has(i)) nxt.delete(i); else nxt.add(i);
                  const arr = Array.from(nxt).sort((a,b) => a-b);
                  setSelectedIndex(arr.length ? arr.join(',') : -1);
                  persistText(arr.join(','));
                } else {
                  const next = selectedIndex === i ? -1 : i;
                  setSelectedIndex(next);
                  persistSelected(next);
                }
              };
              return (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    {q.options.map((cap, i) => {
                      const on = q.multi_select ? active.has(i) : selectedIndex === i;
                      const imgUrl = backendUrl(q.options_images[i] || '');
                      return (
                        <button key={i} type="button"
                          data-testid={`exercise-mcq-picture-option-${q.id}-${i}`}
                          onClick={() => toggle(i)}
                          className={`rounded-xl border-2 p-2 text-left flex flex-col items-center gap-1 ${
                            on ? 'border-[#7B3FA0] bg-[#F3E8FF]' : 'border-slate-200 bg-white hover:border-[#7B3FA0]/60'
                          }`}>
                          <span className="w-full flex items-center gap-1 text-[10px] font-black text-[#5B2A7A]">
                            <span className={`inline-flex items-center justify-center w-4 h-4 rounded border ${on ? 'border-[#7B3FA0] bg-[#7B3FA0] text-white' : 'border-slate-300 bg-white text-transparent'}`}>{q.multi_select ? '✓' : (on ? '●' : '')}</span>
                            {labelFor(i)}
                            <span className="ml-1 truncate font-bold text-[#2B2140]">{cap}</span>
                          </span>
                          {imgUrl ? (
                            <img src={imgUrl} alt={cap}
                              onClick={(e) => { e.stopPropagation(); onOpenImage && onOpenImage(imgUrl); }}
                              className="w-full h-24 object-contain bg-white rounded"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
                          ) : (
                            <div className="w-full h-24 flex items-center justify-center bg-slate-100 rounded text-[10px] text-slate-500 italic">
                              (picture not loaded)
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {q.multi_select && (
                    <p className="text-[11px] italic text-slate-500 mt-1">
                      Tip: you can tick more than one option. / एक से ज़्यादा option choose कर सकते हैं।
                    </p>
                  )}
                  {(selectedIndex !== -1) && <div className="pt-1"><ClearBtn testId={`exercise-clear-${q.id}`} onClear={clearAll}/></div>}
                </>
              );
            })() : (() => {
              const useTextbookLabels = Array.isArray(q.option_labels) && q.option_labels.length === q.options.length;
              const labelFor = (i) => (useTextbookLabels ? q.option_labels[i] : `${String.fromCharCode(65 + i)}.`);
              if (q.multi_select) {
                const parsed = typeof selectedIndex === 'string'
                  ? selectedIndex.split(',').filter(Boolean).map(Number)
                  : (typeof selectedIndex === 'number' && selectedIndex >= 0 ? [selectedIndex] : []);
                const active = new Set(parsed);
                return q.options.map((opt, i) => {
                  const on = active.has(i);
                  return (
                    <button key={i} type="button"
                      data-testid={`exercise-mcq-option-${q.id}-${i}`}
                      onClick={() => {
                        const nxt = new Set(active);
                        if (nxt.has(i)) nxt.delete(i); else nxt.add(i);
                        const arr = Array.from(nxt).sort((a,b) => a-b);
                        const serialised = arr.length ? arr.join(',') : -1;
                        setSelectedIndex(serialised === -1 ? -1 : serialised);
                        persistText(arr.join(','));
                      }}
                      className={`w-full text-left px-3 py-2 rounded-xl border-2 text-sm md:text-base font-bold break-words flex items-start gap-2 ${
                        on ? 'border-[#7B3FA0] bg-[#F3E8FF] text-[#5B2A7A]' : 'border-slate-200 bg-white text-[#2B2140] hover:border-[#7B3FA0]/60'
                      }`}>
                      <span className={`mt-0.5 shrink-0 inline-flex items-center justify-center w-5 h-5 rounded border-2 text-[10px] font-black ${on ? 'border-[#7B3FA0] bg-[#7B3FA0] text-white' : 'border-slate-300 bg-white text-transparent'}`}>✓</span>
                      <span className="font-black mr-1">{labelFor(i)}</span>
                      <span>{opt}</span>
                    </button>
                  );
                });
              }
              return q.options.map((opt, i) => (
                <button key={i} type="button"
                  data-testid={`exercise-mcq-option-${q.id}-${i}`}
                  onClick={() => {
                    const next = selectedIndex === i ? -1 : i;
                    setSelectedIndex(next);
                    persistSelected(next);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-xl border-2 text-sm md:text-base font-bold break-words ${
                    selectedIndex === i
                      ? 'border-[#7B3FA0] bg-[#F3E8FF] text-[#5B2A7A]'
                      : 'border-slate-200 bg-white text-[#2B2140] hover:border-[#7B3FA0]/60'
                  }`}>
                  <span className="mr-2 font-black">{labelFor(i)}</span>{opt}
                </button>
              ));
            })()}
            {!q.options_are_pictures && (selectedIndex !== -1) && <div className="pt-1"><ClearBtn testId={`exercise-clear-${q.id}`} onClear={clearAll}/></div>}
            {!q.options_are_pictures && q.multi_select && (
              <p className="text-[11px] italic text-slate-500 mt-1">
                Tip: you can tick more than one option. / एक से ज़्यादा option choose कर सकते हैं।
              </p>
            )}
          </div>
        )}
        {isMatch && (
          <div>
            {/* Iter-52.55.5 — precompute per-row picture flags so both
                the grid rendering AND the rewrite section agree on
                what's a picture vs a text item. */}
            {(() => null)()}
            <div data-testid={`exercise-match-grid-${q.id}`} className="grid grid-cols-2 gap-x-2 gap-y-2">
              <div className="text-[10px] font-extrabold uppercase tracking-wide text-[#B85582] px-1">Column A</div>
              <div className="text-[10px] font-extrabold uppercase tracking-wide text-[#3577B8] px-1">Column B</div>
              {Array.from({ length: Math.max(q.left.length, q.right.length) }).map((_, i) => {
                const leftItem = q.left[i];
                const rightItem = q.right[i];
                const leftImg = q.left_images?.[i] || '';
                const isPic = isPictureItem(leftItem, leftImg);
                const isLeftSel = selectedLeft === i;
                const pairedTo = pairs[i];
                const rPairedFromLeftEntry = Object.entries(pairs).find(([, v]) => v === i);
                const rPairedFromLeft = rPairedFromLeftEntry ? rPairedFromLeftEntry[0] : null;
                // Iter-52.55.4 — every distinct pair gets ONE colour so the
                // child can see instantly which A ↔ B belong together. We
                // colour by the LEFT item index (stable per pair).
                const PAIR_PALETTE = [
                  { bg: 'bg-[#F3E8FF]', border: 'border-[#7B3FA0]', text: 'text-[#5B2A7A]' },
                  { bg: 'bg-[#DBEAFE]', border: 'border-[#2563EB]', text: 'text-[#1E3A8A]' },
                  { bg: 'bg-[#FEE2E2]', border: 'border-[#DC2626]', text: 'text-[#7F1D1D]' },
                  { bg: 'bg-[#FEF3C7]', border: 'border-[#D97706]', text: 'text-[#7C2D12]' },
                  { bg: 'bg-[#DCFCE7]', border: 'border-[#15803D]', text: 'text-[#14532D]' },
                  { bg: 'bg-[#FCE7F3]', border: 'border-[#BE185D]', text: 'text-[#831843]' },
                  { bg: 'bg-[#E0E7FF]', border: 'border-[#4338CA]', text: 'text-[#312E81]' },
                  { bg: 'bg-[#CCFBF1]', border: 'border-[#0F766E]', text: 'text-[#134E4A]' },
                ];
                const leftPalette = pairedTo != null ? PAIR_PALETTE[i % PAIR_PALETTE.length] : null;
                const rightPalette = rPairedFromLeft != null ? PAIR_PALETTE[Number(rPairedFromLeft) % PAIR_PALETTE.length] : null;
                return (
                  <Fragment key={i}>
                    <button type="button"
                      data-testid={`exercise-match-left-${q.id}-${i}`}
                      onClick={() => {
                        // Iter-52.55.4 — tap a paired item to unpair it.
                        if (pairedTo != null) {
                          setPairs(prev => {
                            const next = { ...prev };
                            delete next[i];
                            persistPairs(next);
                            return next;
                          });
                          setSelectedLeft(null);
                        } else {
                          handleLeftTap(i);
                        }
                      }}
                      disabled={leftItem == null}
                      aria-label={isPic ? `Picture ${i + 1}` : leftItem}
                      className={`text-left rounded-xl p-2 border-2 min-h-[3rem] text-sm md:text-base font-bold text-[#2B2140] break-words flex ${
                        isPic ? 'flex-col items-stretch gap-1' : 'items-center gap-2'
                      } ${
                        isLeftSel ? 'border-[#B85582] bg-[#FCE7F3]'
                        : leftPalette ? `${leftPalette.border} ${leftPalette.bg}`
                        : 'border-slate-200 bg-[#FFF7EA] hover:border-[#B85582]/50'
                      }`}>
                      {isPic ? (
                        <>
                          <span className="flex items-center justify-between">
                            <span className="text-[#FF6B1A] shrink-0 text-xs font-black">{i + 1}.</span>
                            {pairedTo != null && (
                              <span className={`shrink-0 whitespace-nowrap text-[10px] font-black px-1.5 py-0.5 rounded ${leftPalette ? leftPalette.text : 'text-[#7B3FA0]'}`}
                                data-testid={`exercise-match-pair-badge-left-${q.id}-${i}`}>
                                {i + 1} → {String.fromCharCode(65 + pairedTo)}
                              </span>
                            )}
                          </span>
                          {leftImg ? (
                            <img src={leftImg} alt=""
                              data-testid={`exercise-match-picture-${q.id}-${i}`}
                              onClick={(e) => { e.stopPropagation(); onOpenImage && onOpenImage(leftImg); }}
                              className="w-full h-32 object-contain bg-white rounded"
                              onError={(e) => {
                                const parent = e.currentTarget.parentElement;
                                e.currentTarget.style.display = 'none';
                                // Swap to a compact fallback if the crop
                                // permanently fails (bad URL / storage 404).
                                const fb = parent.querySelector('[data-crop-fallback]');
                                if (fb) fb.style.display = 'flex';
                              }}/>
                          ) : null}
                          {!leftImg && (
                            <div data-testid={`exercise-match-picture-loading-${q.id}-${i}`}
                              className="w-full h-32 flex items-center justify-center bg-white/70 rounded text-[10px] font-bold text-slate-600 italic text-center px-2">
                              🖼️ Picture load ho rahi hai… / चित्र लोड हो रहा है…
                            </div>
                          )}
                          {leftImg && pageImage && (
                            <div data-crop-fallback style={{ display: 'none' }}
                              className="w-full h-32 flex-col items-center justify-center bg-white/80 rounded text-[10px] font-bold text-amber-800 italic text-center px-2">
                              🖼️ Picture is in your textbook page — tap to view
                              <button type="button" onClick={(e) => { e.stopPropagation(); onOpenImage && onOpenImage(pageImage); }}
                                className="mt-1 underline">Open</button>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="text-[#FF6B1A] shrink-0 text-xs">{i + 1}.</span>
                          <span className="min-w-0 break-words flex-1">{leftItem}</span>
                          {pairedTo != null && (
                            <span className={`ml-auto shrink-0 whitespace-nowrap text-[10px] font-black px-1.5 py-0.5 rounded ${leftPalette ? leftPalette.text : 'text-[#7B3FA0]'}`}
                              data-testid={`exercise-match-pair-badge-left-${q.id}-${i}`}>
                              {i + 1} → {String.fromCharCode(65 + pairedTo)}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                    <button type="button"
                      data-testid={`exercise-match-right-${q.id}-${i}`}
                      onClick={() => {
                        // Iter-52.55.4 — tap a paired right item to unpair it.
                        if (rPairedFromLeft != null) {
                          setPairs(prev => {
                            const next = { ...prev };
                            delete next[Number(rPairedFromLeft)];
                            persistPairs(next);
                            return next;
                          });
                        } else {
                          handleRightTap(i);
                        }
                      }}
                      disabled={rightItem == null || (selectedLeft == null && rPairedFromLeft == null)}
                      className={`text-left rounded-xl p-2 border-2 min-h-[3rem] text-sm md:text-base font-bold text-[#2B2140] break-words flex items-center gap-2 ${
                        rightPalette ? `${rightPalette.border} ${rightPalette.bg}`
                        : selectedLeft != null ? 'border-slate-300 bg-white hover:border-[#3577B8] cursor-pointer'
                        : 'border-slate-200 bg-white opacity-70'
                      }`}>
                      <span className="text-[#3577B8] shrink-0 text-xs">{String.fromCharCode(65 + i)}.</span>
                      <span className="flex-1 min-w-0 break-words">{rightItem}</span>
                      {rPairedFromLeft != null && (
                        <span className={`ml-auto shrink-0 whitespace-nowrap text-[10px] font-black px-1.5 py-0.5 rounded ${rightPalette ? rightPalette.text : 'text-[#7B3FA0]'}`}
                          data-testid={`exercise-match-pair-badge-right-${q.id}-${i}`}>
                          {Number(rPairedFromLeft) + 1} → {String.fromCharCode(65 + i)}
                        </span>
                      )}
                    </button>
                  </Fragment>
                );
              })}
            </div>
            <div className="mt-2 flex items-start justify-between gap-2">
              <p data-testid={`exercise-match-instruction-${q.id}`}
                className="text-xs md:text-sm font-semibold text-[#2B2140] leading-snug flex-1 break-words">
                {selectedLeft == null
                  ? 'Pehle Column A ka word tap karo, phir Column B mein uska jodi-wala word. / पहले Column A का शब्द टैप करें, फिर Column B में उसका जोड़ा।'
                  : (isPictureItem(q.left[selectedLeft], q.left_images?.[selectedLeft])
                      ? `Ab Column B mein picture ${selectedLeft + 1} ka jodi-wala item tap karo. / अब Column B में उसका जोड़ा टैप करें।`
                      : `Ab Column B mein "${q.left[selectedLeft]}" ka jodi-wala item tap karo. / अब Column B में इसका जोड़ा टैप करें।`)}
              </p>
              {Object.keys(pairs).length > 0 && <ClearBtn testId={`exercise-clear-${q.id}`} onClear={clearAll}/>}
            </div>
            {q.has_rewrite && (
              <RewriteSection
                q={q}
                childId={childId} bookId={bookId} pageId={pageId}
                initialRewriteTexts={initialScratch?.rewrite_texts || {}}
                pageImage={pageImage}
                onOpenImage={onOpenImage}
                onScratchChange={notifyChange}
                tts={tts} voiceGender={voiceGender}
                activePlaybackId={activePlaybackId}
                onPlayStart={onPlayStart} onWordChange={onWordChange} onWordTap={onWordTap}
              />
            )}
          </div>
        )}
        {hiddenWrite && (
          <div>
            {!expandedWrite ? (
              <button type="button"
                data-testid={`exercise-write-toggle-${q.id}`}
                onClick={() => setExpandedWrite(true)}
                className="inline-flex items-center gap-1.5 text-xs md:text-sm font-extrabold text-[#5B8DEF] hover:text-[#3577B8]">
                <Pencil size={13} strokeWidth={2.5}/> ✍️ Write your answer / उत्तर लिखें
              </button>
            ) : (
              <div className="space-y-2">
                <textarea data-testid={`exercise-attempt-${q.id}`}
                  value={attempt}
                  onChange={e => { setAttempt(e.target.value); persistText(e.target.value); }}
                  placeholder="Write your answer here…"
                  rows={3}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 text-sm md:text-base font-medium text-[#2B2140] focus:outline-none focus:border-[#FF6B1A] resize-none text-left"
                  style={{ hyphens: 'auto' }}/>
                <div className="flex items-center gap-2">
                  <ClearBtn testId={`exercise-clear-${q.id}`} onClear={clearAll} disabled={!attempt}/>
                  <button type="button" onClick={() => setExpandedWrite(false)}
                    className="text-[11px] font-bold text-slate-500 hover:text-slate-800 ml-auto">
                    Close writing space
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {verdictInfo && (
        <VerdictBadge
          verdict={verdictInfo.verdict}
          feedback={verdictInfo.feedback}
          questionId={q.id}
          tts={tts} voiceGender={voiceGender}
          activePlaybackId={activePlaybackId}
          onPlayStart={onPlayStart} onWordChange={onWordChange}
        />
      )}
    </div>
  );
}

/**
 * Groups a flat exercises array by section_key while preserving the
 * page's textbook order via section_order (fallbacks: first-index).
 */
export function groupExercisesBySection(exercises) {
  if (!exercises || exercises.length === 0) return [];
  const map = new Map();
  exercises.forEach((q, i) => {
    const key = q.section_key || q.type || `sec_${i}`;
    if (!map.has(key)) {
      map.set(key, {
        section_key: key,
        section_heading: q.section_heading || '',
        section_heading_meaning: q.section_heading_meaning || '',
        section_order: (typeof q.section_order === 'number') ? q.section_order : i,
        first_index: i,
        items: [],
      });
    }
    map.get(key).items.push(q);
  });
  return Array.from(map.values()).sort((a, b) => {
    if (a.section_order !== b.section_order) return a.section_order - b.section_order;
    return a.first_index - b.first_index;
  });
}

// ─── Iter-52.55.4 — Section-level "Check my answers" ─────────────────
//
// Objective grading is LOCAL — no LLM call. Long-answer / critical_thinking
// hit the batched /exercises/check-batch endpoint (Claude Haiku 4.5).
// Results are cached per (child, page, section) via /exercises/check-results.

const normaliseAnswer = (v) => String(v || '')
  .toLowerCase()
  .replace(/[.,;:!?।॥"'“”‘’()\[\]{}\-—–]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/** Grade a single question locally. Returns {verdict, feedback, snapshot}
 *  or null when the type needs AI (long_answer, critical_thinking). */
export function localGradeQuestion(q, scratch) {
  if (!q) return null;
  const type = q.type;
  if (type === 'long_answer' || type === 'critical_thinking') return null;
  if (type === 'fill_blank' || type === 'unscramble') {
    const child = String(scratch?.text || '').trim();
    if (!child) return { verdict: 'incorrect', feedback: 'Yeh empty hai — likho aur try karo. / यह खाली है।', snapshot: '' };
    const key = String(q.answer || '').trim();
    if (!key) return { verdict: 'unchecked', feedback: 'Answer key nahi mili. / Answer key not available.', snapshot: child };
    const ok = normaliseAnswer(child) === normaliseAnswer(key);
    return {
      verdict: ok ? 'correct' : 'incorrect',
      feedback: ok ? 'Sahi hai! / सही है!' : 'Answer Key dekh kar sikh lo. / Answer Key देख कर सीखें।',
      snapshot: child,
    };
  }
  if (type === 'mcq') {
    const sel = scratch?.selected;
    if (q.multi_select) {
      const raw = String(scratch?.text || '').trim();
      const child = raw ? raw.split(',').filter(Boolean).map(Number).sort() : [];
      const key = Array.isArray(q.answer_indices) ? [...q.answer_indices].sort() : [];
      if (!child.length) return { verdict: 'incorrect', feedback: 'Koi option tap nahi kiya. / कोई option नहीं चुना।', snapshot: '' };
      const ok = child.length === key.length && child.every((v, i) => v === key[i]);
      return {
        verdict: ok ? 'correct' : 'incorrect',
        feedback: ok ? 'Sahi hai! / सही है!' : 'Ek baar phir se soch kar tap karo. / एक बार फिर सोचें।',
        snapshot: raw,
      };
    }
    if (typeof sel !== 'number' || sel < 0) return { verdict: 'incorrect', feedback: 'Koi option tap nahi kiya. / कोई option नहीं चुना।', snapshot: '' };
    const ok = sel === Number(q.answer_index);
    return {
      verdict: ok ? 'correct' : 'incorrect',
      feedback: ok ? 'Sahi hai! / सही है!' : 'Dobara try karo. / दोबारा कोशिश करें।',
      snapshot: String(sel),
    };
  }
  if (type === 'match_the_following') {
    const pairs = scratch?.pairs || {};
    const key = q.answer_pairs || [];
    if (!key.length) return { verdict: 'unchecked', feedback: 'Answer key nahi mili. / Answer key not available.', snapshot: '' };
    const child = key.map((_, i) => pairs[i]);
    if (child.every(v => v == null || v === undefined)) {
      return { verdict: 'incorrect', feedback: 'Koi jodi nahi banayi. / कोई जोड़ी नहीं बनाई।', snapshot: '' };
    }
    const okCount = key.reduce((acc, k, i) => acc + (Number(child[i]) === Number(k) ? 1 : 0), 0);
    const ok = okCount === key.length;
    return {
      verdict: ok ? 'correct' : 'incorrect',
      feedback: ok
        ? `Saari jodiyaan sahi hain! / सारी जोड़ियाँ सही!`
        : `${okCount}/${key.length} sahi. Baaki dekh kar sikh lo. / ${okCount}/${key.length} सही।`,
      snapshot: JSON.stringify(child),
    };
  }
  return null;
}

function SectionChecker({
  section, scratchAnswers, scratchSelected, scratchPairs,
  childId, bookId, pageId, language,
  onResultsChange, sectionResult, checksLeftToday, isAdmin,
  onChecksLeftChange,
}) {
  const [busy, setBusy] = useState(false);
  const [limitToast, setLimitToast] = useState('');
  const items = section.items || [];
  const hasSubjective = items.some(q => q.type === 'long_answer' || q.type === 'critical_thinking');

  const runCheck = async () => {
    if (busy) return;
    setBusy(true); setLimitToast('');
    try {
      const perItem = items.map(q => {
        const scratch = {
          text: scratchAnswers[q.id] || '',
          selected: scratchSelected[q.id] ?? -1,
          pairs: scratchPairs[q.id] || {},
        };
        const local = localGradeQuestion(q, scratch);
        return { q, scratch, local };
      });
      const results = perItem.map(({ q, local }) => local
        ? { question_id: q.id, verdict: local.verdict, feedback: local.feedback, answer_snapshot: local.snapshot }
        : { question_id: q.id, verdict: 'pending', feedback: '', answer_snapshot: scratchAnswers[q.id] || '' }
      );
      const subjective = perItem.filter(({ q, scratch }) =>
        (q.type === 'long_answer' || q.type === 'critical_thinking') && (scratch.text || '').trim()
      );
      if (subjective.length > 0) {
        try {
          const { data } = await api.post('/exercises/check-batch', {
            child_id: childId, book_id: bookId, page_id: pageId,
            section_key: section.section_key, language: language || 'en',
            items: subjective.map(({ q, scratch }) => ({
              question_id: q.id, question: q.question,
              answer_key: q.answer || '', child_answer: scratch.text || '',
              question_type: q.type,
            })),
          });
          if (data.limit_reached) {
            setLimitToast('Aaj ke liye AI-check limit poori ho gayi (5/day). Kal fir se try karein.');
          }
          if (typeof data.checks_left_today === 'number' || data.checks_left_today === null) {
            onChecksLeftChange && onChecksLeftChange(data.checks_left_today);
          }
          const byId = new Map((data.results || []).map(r => [r.question_id, r]));
          results.forEach(r => {
            if (r.verdict !== 'pending') return;
            const rmt = byId.get(r.question_id);
            if (rmt) { r.verdict = rmt.verdict; r.feedback = rmt.feedback; }
            else { r.verdict = 'unchecked'; r.feedback = 'Nahi check ho paya. / Couldn\'t check yet.'; }
          });
        } catch (err) {
          const msg = err?.response?.data?.detail || 'AI check abhi busy hai, thodi der mein try karo.';
          toast.error(msg);
          results.forEach(r => { if (r.verdict === 'pending') { r.verdict = 'unchecked'; r.feedback = ''; } });
        }
      } else {
        results.forEach(r => { if (r.verdict === 'pending') { r.verdict = 'unchecked'; r.feedback = ''; } });
      }
      // Empty-answer detection for the "you didn't try this yet" badge.
      const answeredIds = new Set(perItem
        .filter(({ q, scratch }) => q.type === 'long_answer' || q.type === 'critical_thinking'
          ? (scratch.text || '').trim()
          : (q.type === 'fill_blank' || q.type === 'unscramble' ? (scratch.text || '').trim()
             : q.type === 'mcq' ? (q.multi_select ? (scratch.text || '').trim() : (scratch.selected ?? -1) >= 0)
             : q.type === 'match_the_following' ? Object.keys(scratch.pairs || {}).length > 0
             : true))
        .map(({ q }) => q.id));
      results.forEach(r => {
        if (!answeredIds.has(r.question_id) && r.verdict === 'unchecked') {
          r.verdict = 'empty';
          r.feedback = 'Empty hai — pehle likho phir check karo. / यह खाली है।';
        }
      });
      const correct = results.filter(r => r.verdict === 'sahi' || r.verdict === 'correct').length;
      const total = results.length;
      try {
        await api.post('/exercises/check-results', {
          child_id: childId, book_id: bookId, page_id: pageId,
          section_key: section.section_key,
          results, score_correct: correct, score_total: total,
        });
      } catch {}
      onResultsChange && onResultsChange(section.section_key, {
        results, score_correct: correct, score_total: total,
      });
    } finally {
      setBusy(false);
    }
  };

  const score = sectionResult?.score_total ? `${sectionResult.score_correct}/${sectionResult.score_total}` : null;
  const celebrationEmoji = (() => {
    if (!sectionResult?.score_total) return '';
    const pct = sectionResult.score_correct / sectionResult.score_total;
    if (pct >= 0.9) return '🎉';
    if (pct >= 0.6) return '👍';
    return '💪';
  })();

  return (
    <div className="mt-2 space-y-1" data-testid={`section-checker-${section.section_key}`}>
      {score && (
        <div data-testid={`section-score-${section.section_key}`}
          className="inline-flex items-center gap-2 rounded-xl px-3 py-1 bg-emerald-50 border border-emerald-300 text-sm md:text-base font-black text-emerald-900">
          <span>Score:</span><span>{score}</span><span>{celebrationEmoji}</span>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button"
          data-testid={`section-check-btn-${section.section_key}`}
          onClick={runCheck}
          disabled={busy}
          className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm md:text-base font-black border-2 ${
            busy
              ? 'bg-slate-100 border-slate-300 text-slate-500'
              : 'bg-[#FFF7EA] border-[#FF6B1A] text-[#7C3B00] hover:bg-[#FFE9CE]'
          }`}>
          {busy ? <Loader2 size={14} className="animate-spin"/> : <span>✅</span>}
          {busy ? 'Checking… / जाँच रहे हैं…' : 'Check my answers / जाँचें'}
        </button>
        {hasSubjective && !isAdmin && typeof checksLeftToday === 'number' && (
          <span data-testid={`section-checks-left-${section.section_key}`}
            className="text-[11px] font-bold text-slate-600">
            {checksLeftToday} AI check{checksLeftToday === 1 ? '' : 's'} left today
          </span>
        )}
        {hasSubjective && isAdmin && (
          <span className="text-[11px] font-bold text-emerald-700">Admin — no daily cap</span>
        )}
      </div>
      {limitToast && (
        <p data-testid={`section-limit-warning-${section.section_key}`}
          className="text-xs font-bold text-amber-800 bg-amber-50 border border-amber-300 rounded-xl px-2 py-1">
          {limitToast}
        </p>
      )}
    </div>
  );
}

function VerdictBadge({ verdict, feedback, questionId, tts, voiceGender,
  activePlaybackId, onPlayStart, onWordChange }) {
  if (!verdict || verdict === 'pending' || verdict === 'unchecked') return null;
  const isOk = verdict === 'sahi' || verdict === 'correct';
  const isPartial = verdict === 'thoda';
  const isEmpty = verdict === 'empty';
  const emoji = isOk ? '✓' : (isPartial ? '~' : (isEmpty ? '·' : '✗'));
  const palette = isOk
    ? 'bg-emerald-50 border-emerald-400 text-emerald-900'
    : isPartial
      ? 'bg-amber-50 border-amber-400 text-amber-900'
      : 'bg-rose-50 border-rose-400 text-rose-900';
  return (
    <div className={`mt-2 rounded-xl border-2 px-2 py-1 flex items-start gap-2 ${palette}`}
      data-testid={`verdict-badge-${questionId}`}>
      <span className="text-lg font-black shrink-0">{emoji}</span>
      <div className="flex-1 min-w-0">
        {feedback && (
          <SpokenText
            text={feedback}
            id={`v-${questionId}`}
            activePlaybackId={activePlaybackId}
            onPlayStart={onPlayStart} onWordChange={onWordChange}
            voiceGender={voiceGender} tts={tts}
            testId={`verdict-feedback-${questionId}`}
            className="text-xs md:text-sm font-bold"
          />
        )}
      </div>
    </div>
  );
}



function SectionHeader({ section, tts, voiceGender, activePlaybackId, activeWordIdx, onPlayStart, onWordChange, onWordTap }) {
  const heading = (section.section_heading || '').trim();
  if (!heading) return null;
  return (
    <div data-testid={`exercise-section-header-${section.section_key}`}
      className="mb-2 mt-4 first:mt-0">
      <SpokenText
        text={heading}
        id={`sec-h-${section.section_key}`}
        activePlaybackId={activePlaybackId}
        activeWordIdx={activeWordIdx}
        onPlayStart={onPlayStart} onWordChange={onWordChange} onWordTap={onWordTap}
        voiceGender={voiceGender} tts={tts}
        className="text-base md:text-lg font-black text-[#2B2140] uppercase tracking-wide"
      />
      {section.section_heading_meaning && (
        <div className="ml-8 mt-0.5">
          <SpokenText
            text={section.section_heading_meaning}
            id={`sec-hm-${section.section_key}`}
            activePlaybackId={activePlaybackId}
            activeWordIdx={activeWordIdx}
            onPlayStart={onPlayStart} onWordChange={onWordChange} onWordTap={onWordTap}
            voiceGender={voiceGender} tts={tts}
            className="text-xs md:text-sm italic text-slate-500"
          />
        </div>
      )}
    </div>
  );
}

/** Panel — self-contained, embeddable OR mountable on its own screen. */
export function ExercisesPanel({ bookId, pageId, passage, language, hiFont, childId, matchBlock, voiceGender, pageImage }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exercises, setExercises] = useState([]);
  const [errored, setErrored] = useState(false);
  const [scratch, setScratch] = useState({ answers: {}, selected: {}, pairs: {}, rewrite_texts: {} });
  const [popoverWord, setPopoverWord] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [activePlaybackId, setActivePlaybackId] = useState(null);
  const [activeWordIdx, setActiveWordIdx] = useState(-1);
  // Iter-52.55.4 — per-section check state.
  const [sectionResults, setSectionResults] = useState({});
  const [verdictByQid, setVerdictByQid] = useState({});
  const [checksLeftToday, setChecksLeftToday] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [snapshotByQid, setSnapshotByQid] = useState({});
  // Iter-52.54 — mirror the "who's playing" id in a ref so the
  // onWordChange callback (captured in useTts at play() time) can
  // check against the LATEST id instead of a stale closure. This
  // was the root cause of no karaoke highlight on the Exercises
  // screen: the callback fired but the stale-closure guard bailed
  // before it could set activeWordIdx.
  const activePlaybackIdRef = useRef(null);
  const tts = useTts(0.9);
  const retriedRef = useRef(false);

  const passageKey = passage?.slice(0, 120) || '';
  const matchBlockKey = matchBlock ? JSON.stringify({ l: matchBlock.left, r: matchBlock.right, i: matchBlock.instruction }).slice(0, 200) : '';

  // Iter-52.55.3 — POLL-BASED DETECT.
  //
  // The old two-phase client-driven flow abandoned the server work
  // whenever a phone/laptop lost focus for > 25 s. The new server-
  // side job pipeline (exercise_detect_jobs + text-stage checkpoint
  // in the cache) keeps running regardless of client state — we
  // just poll every 3 s and merge whatever the server has ready.
  //
  //   status → "done"       → final render
  //   status → "partial"    → text ready, cropping in progress
  //   status → "processing" → still detecting (spinner)
  //   status → "failed"     → one silent retry, then error card
  const detectPoll = useCallback(async () => {
    const ctrl = new AbortController();
    // 20 s per poll — safe under Cloudflare's 60 s origin cap.
    const to = setTimeout(() => ctrl.abort(), 20000);
    try {
      const resp = await fetch(`${API}/exercises/detect`, {
        method: 'POST', credentials: 'include', signal: ctrl.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passage: passage || '',
          language: language || 'en',
          cache_key: `${bookId}:${pageId}:${language || 'en'}`,
          match_block: matchBlock || undefined,
          book_id: bookId,
          page_id: pageId,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return await resp.json();
    } finally { clearTimeout(to); }
  }, [bookId, pageId, language, passage, matchBlock]);

  const runDetect = useCallback(async () => {
    if ((!passage || passage.trim().length < 60) && !matchBlock) { setExercises([]); return; }
    setLoading(true); setErrored(false);
    // 3 min wall-clock ceiling = 60 polls at 3 s.
    const deadline = Date.now() + 180000;
    let seenText = false;
    let failCount = 0;
    try {
      while (Date.now() < deadline) {
        let data;
        try { data = await detectPoll(); }
        catch {
          failCount += 1;
          if (failCount >= 3) { setErrored(true); return; }
          await new Promise(r => setTimeout(r, 3000));
          continue;
        }
        failCount = 0;
        const items = Array.isArray(data.exercises)
          ? data.exercises.filter(q => q && q.question && q.question.trim())
          : [];
        if (items.length > 0) {
          setExercises(items);
          seenText = true;
          // Once we have TEXT exercises, take the loader down. We keep
          // polling in the background for the crop stage, but the child
          // can already read and start answering.
          setLoading(false);
        }
        const st = data.status;
        if (st === 'done') return;
        if (st === 'failed') { if (!seenText) setErrored(true); return; }
        // "processing" or "partial" → keep polling every 3 s.
        await new Promise(r => setTimeout(r, 3000));
      }
      // Deadline elapsed.
      if (!seenText) setErrored(true);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, pageId, language, passageKey, matchBlockKey]);

  useEffect(() => { runDetect(); }, [runDetect]);

  useEffect(() => {
    if (!childId || !bookId || !pageId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/exercises/scratch`, {
          params: { child_id: childId, book_id: bookId, page_id: pageId },
        });
        if (!cancelled) setScratch({
          answers: data.answers || {},
          selected: data.selected || {},
          pairs: data.pairs || {},
          rewrite_texts: data.rewrite_texts || {},
        });
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [childId, bookId, pageId]);

  // Iter-52.55.4 — hydrate section check results + AI-check quota.
  useEffect(() => {
    if (!childId || !bookId || !pageId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/exercises/check-results`, {
          params: { child_id: childId, book_id: bookId, page_id: pageId },
        });
        if (cancelled) return;
        setSectionResults(data.sections || {});
        setChecksLeftToday(typeof data.checks_left_today === 'number' ? data.checks_left_today : null);
        setIsAdmin(!!data.is_admin);
        // Build verdict + snapshot maps.
        const vMap = {}, sMap = {};
        Object.values(data.sections || {}).forEach(sec => {
          (sec.results || []).forEach(r => {
            vMap[r.question_id] = { verdict: r.verdict, feedback: r.feedback };
            sMap[r.question_id] = r.answer_snapshot || '';
          });
        });
        setVerdictByQid(vMap);
        setSnapshotByQid(sMap);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [childId, bookId, pageId]);

  const handleSectionResults = (sectionKey, payload) => {
    setSectionResults(prev => ({ ...prev, [sectionKey]: payload }));
    const vMap = { ...verdictByQid };
    const sMap = { ...snapshotByQid };
    (payload.results || []).forEach(r => {
      vMap[r.question_id] = { verdict: r.verdict, feedback: r.feedback };
      sMap[r.question_id] = r.answer_snapshot || '';
    });
    setVerdictByQid(vMap);
    setSnapshotByQid(sMap);
  };

  // Clear a question's ✓/✗ verdict as soon as the child edits/clears it.
  const clearVerdictIfChanged = (qid, currentSnapshot) => {
    if (!verdictByQid[qid]) return;
    if (snapshotByQid[qid] !== currentSnapshot) {
      setVerdictByQid(prev => {
        const nxt = { ...prev }; delete nxt[qid]; return nxt;
      });
    }
  };

  const sections = useMemo(() => groupExercisesBySection(exercises), [exercises]);

  const handlePlayStart = (id) => {
    activePlaybackIdRef.current = id;
    setActivePlaybackId(id);
    setActiveWordIdx(-1);
  };
  const handleWordChange = (idx, id) => {
    // Guard against a stale playback callback firing after the user
    // tapped a different speaker. Compare against the REF, not state,
    // because this callback was captured at play() time.
    if (id && id !== activePlaybackIdRef.current && idx !== -1) return;
    setActiveWordIdx(idx);
    if (idx === -1) {
      activePlaybackIdRef.current = null;
      setActivePlaybackId(null);
    }
  };
  const handleWordTap = (word, sentence) => {
    const clean = String(word || '').replace(/[^\p{L}\p{N}\-']/gu, '');
    if (!clean) return;
    setPopoverWord({ word: clean, language: isDevanagari(sentence) ? 'hi' : 'en', sentence });
  };

  if (!loading && !errored && exercises.length === 0) return null;

  return (
    <div data-testid="exercises-panel" className={`w-full max-w-md mx-auto mt-4 mb-4 ${hiFont || ''}`}>
      <div className="flex items-center gap-2 mb-3">
        <BookOpen size={16} strokeWidth={3} className="text-[#B85582]"/>
        <h3 className="text-sm md:text-base font-extrabold text-[#2B2140]">Exercises / अभ्यास</h3>
        <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wide">
          {loading ? 'finding…' : `${exercises.length} question${exercises.length === 1 ? '' : 's'}`}
        </span>
      </div>
      {loading && (
        <div data-testid="exercises-loading"
          className="bg-white rounded-3xl p-4 flex items-start gap-3 text-sm text-slate-600 border border-[#FFB84D]/40 shadow-sm">
          <Loader2 size={18} className="animate-spin text-[#FF6B1A] mt-0.5 shrink-0"/>
          <div className="flex-1">
            <p className="font-bold text-[#2B2140]">
              Questions aur answers tayyar ho rahe hain…
            </p>
            <p className="text-xs mt-1 italic text-slate-500">
              प्रश्न और उत्तर तैयार हो रहे हैं, इसमें 30–60 सेकंड लग सकते हैं। कृपया रुकें।
            </p>
            <p className="text-xs mt-1 italic text-slate-500">
              Please wait — this can take 30–60 seconds. 🙏
            </p>
          </div>
        </div>
      )}
      {!loading && errored && (
        <button data-testid="exercises-retry" onClick={() => { retriedRef.current = false; runDetect(); }}
          className="w-full bg-white rounded-3xl p-4 text-left font-bold text-[#B85582] border border-[#B85582]/30 hover:bg-[#B85582]/5">
          <span className="block text-sm">
            Thoda zyada time lag raha hai — kripya page refresh karein.
          </span>
          <span className="block text-xs italic text-slate-500 mt-1">
            कृपया पेज रीफ़्रेश करें।
          </span>
          <span className="block text-xs italic text-slate-500 mt-0.5">
            Taking longer than usual — please refresh the page or tap here to retry.
          </span>
        </button>
      )}
      {!loading && !errored && exercises.length > 0 && (
        <div className="space-y-2">
          {sections.map(section => (
            <section key={section.section_key} data-testid={`exercise-section-${section.section_key}`}>
              <SectionHeader section={section} tts={tts} voiceGender={voiceGender}
                activePlaybackId={activePlaybackId} activeWordIdx={activeWordIdx}
                onPlayStart={handlePlayStart} onWordChange={handleWordChange} onWordTap={handleWordTap}/>
              <div className="space-y-2">
                {section.items.map(q => (
                  <ExerciseCard key={q.id} q={q} tts={tts}
                    childId={childId} bookId={bookId} pageId={pageId} voiceGender={voiceGender}
                    pageImage={pageImage} onOpenImage={(src) => setLightboxSrc(src)}
                    activePlaybackId={activePlaybackId} activeWordIdx={activeWordIdx}
                    onPlayStart={handlePlayStart} onWordChange={handleWordChange} onWordTap={handleWordTap}
                    verdictInfo={verdictByQid[q.id]}
                    onScratchChange={(snapshot) => clearVerdictIfChanged(q.id, snapshot)}
                    initialScratch={{
                      text: scratch?.answers?.[q.id] || '',
                      selected: scratch?.selected?.[q.id] ?? -1,
                      pairs: scratch?.pairs?.[q.id] || {},
                      rewrite_texts: scratch?.rewrite_texts?.[q.id] || {},
                    }}/>
                ))}
              </div>
              {childId && bookId && pageId && (
                <SectionChecker
                  section={section}
                  scratchAnswers={scratch.answers || {}}
                  scratchSelected={scratch.selected || {}}
                  scratchPairs={scratch.pairs || {}}
                  childId={childId} bookId={bookId} pageId={pageId} language={language}
                  onResultsChange={handleSectionResults}
                  sectionResult={sectionResults[section.section_key]}
                  checksLeftToday={checksLeftToday}
                  isAdmin={isAdmin}
                  onChecksLeftChange={setChecksLeftToday}
                />
              )}
            </section>
          ))}
          <div className="pt-2">
            <button type="button"
              data-testid="exercises-open-answer-key"
              onClick={() => nav(`/exercises/${bookId}/${pageId}/answers`)}
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 bg-emerald-50 border-2 border-emerald-500/40 text-emerald-800 font-black hover:bg-emerald-100 hover:border-emerald-500/70">
              <KeyRound size={16} strokeWidth={3}/>
              🔑 See Answer Key / उत्तर कुंजी देखें
            </button>
            <p className="text-[11px] italic text-slate-500 mt-1 text-center">
              Try it yourself first! / पहले खुद कोशिश करें।
            </p>
          </div>
        </div>
      )}

      {lightboxSrc && (
        <div
          data-testid="exercises-image-lightbox"
          onClick={() => setLightboxSrc(null)}
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
        >
          <img src={lightboxSrc} alt="Textbook page"
            className="max-w-full max-h-full object-contain rounded shadow-xl"/>
          <button type="button" onClick={() => setLightboxSrc(null)}
            className="absolute top-3 right-3 rounded-full bg-white/90 text-slate-800 px-3 py-1 text-sm font-extrabold">
            Close
          </button>
        </div>
      )}

      {popoverWord && (
        <SyllablePopover
          word={popoverWord.word}
          language={popoverWord.language}
          sentence={popoverWord.sentence}
          wordIndex={0}
          ttsPlay={tts.play}
          voiceGender={voiceGender || 'female'}
          onClose={() => setPopoverWord(null)}
        />
      )}
    </div>
  );
}

export default ExercisesPanel;
